import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  createRootRouteWithContext,
  HeadContent,
  Scripts,
  useLocation,
  useNavigate,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";
import { supabase } from "@/lib/supabase";
import { pingSupabaseFromClient } from "@/lib/keepalive";

import appCss from "../design-system/styles.css?url";
import { CartProvider } from "@/features/cart/store/cart-store";
import { SiteHeader } from "@/components/layout/site-header";
import { SiteFooter } from "@/components/layout/site-footer";
import { CartDrawer } from "@/features/cart/components/cart-drawer";
import { MobileTabBar } from "@/components/layout/mobile-tab-bar";
import { WhatsAppSupportButton } from "@/components/common/WhatsAppSupportButton";
import { NotFound } from "@/components/common/not-found";
import { ErrorFallback } from "@/components/common/error-fallback";
import { getOrganizationSchema, getWebSiteSchema, safeJsonLd } from "@/lib/seo";
import { trackMetaPageView } from "@/lib/meta-pixel";
import { siteConfig } from "@/config/site";
import { fontLinks, fontNoscriptHref } from "@/design-system/fonts";

/**
 * Open Graph and Twitter both require an ABSOLUTE image URL -- a relative path is
 * silently dropped by every crawler, which is why shares had no preview image even
 * on routes that set one.
 */
const defaultOgImage = `${siteConfig.url}${siteConfig.ogImage}`;

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "theme-color", content: "#fbf9f4" },
      { title: siteConfig.seo.defaultTitle },
      { name: "description", content: siteConfig.seo.defaultDescription },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: siteConfig.name },
      { property: "og:title", content: siteConfig.seo.defaultTitle },
      {
        property: "og:description",
        content: siteConfig.seo.defaultDescription,
      },
      { property: "og:url", content: siteConfig.url },
      { property: "og:locale", content: "en_IN" },
      { property: "og:image", content: defaultOgImage },
      // og:image:width/height are deliberately NOT set here. Head meta merges by key, so a
      // dimension declared at root cannot be unset by a child route -- and product.$id and
      // blog/$slug both override og:image with their own artwork (product photos are square:
      // 738, 938 and 1254px variants exist). Declaring 1200x630 globally told every crawler
      // the wrong geometry for exactly the URLs people actually share, and scrapers lay the
      // preview card out from the hint before fetching the file. Omitted is accurate; the
      // crawler measures the image itself. Pages that DO serve the 1200x630 default declare
      // it themselves -- see index.tsx.
      { property: "og:image:alt", content: `${siteConfig.name} — ${siteConfig.subtitle}` },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: siteConfig.seo.defaultTitle },
      {
        name: "twitter:description",
        content: siteConfig.seo.defaultDescription,
      },
      { name: "twitter:image", content: defaultOgImage },
      { name: "theme-color", content: "#f5f0e8" },
      {
        name: "keywords",
        content:
          "Aster Decorz, asterdecorz, asterdecorz.com, artificial flowers, fake plants, silk bonsai, artificial plants online India",
      },
      ...(import.meta.env.VITE_GOOGLE_SITE_VERIFICATION
        ? [
            {
              name: "google-site-verification",
              content: import.meta.env.VITE_GOOGLE_SITE_VERIFICATION,
            },
          ]
        : []),
    ],
    links: [
      // preconnect hints MUST come first — they fire DNS/TLS handshakes early.
      // fontLinks exports the two preconnect entries before the stylesheet entry,
      // so spreading it first ensures they appear in the head before appCss.
      ...fontLinks,
      { rel: "stylesheet", href: appCss },
      { rel: "icon", href: "/favicon.ico?v=2", type: "image/x-icon" },
      { rel: "icon", href: "/favicon-32x32.png?v=2", type: "image/png", sizes: "32x32" },
      { rel: "apple-touch-icon", href: "/apple-touch-icon.png?v=2", sizes: "180x180" },
      // NOTE: deliberately no `canonical` here. A canonical on the root route applies to
      // every match beneath it, so pointing it at siteConfig.url told crawlers that /shop,
      // /about, /collections and /blog were all duplicates of the homepage. Canonical is
      // now declared per route, next to that route's own title and description.
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFound,
  errorComponent: ErrorFallback,
});

/**
 * The document shell.
 *
 * Registered as `shellComponent`, which TanStack Router renders around the root
 * match's entire subtree -- so this owns <html>/<head>/<body> and `children` is the
 * app (RootComponent and everything under Outlet). The client entry hydrates
 * `document`, so this must render identically on the server and in the browser:
 * there is deliberately no `typeof window` branch. An earlier revision returned
 * `<>{children}</>` in the browser to avoid nesting <html> inside #root, but the
 * component was never wired up at all, so <HeadContent /> and both JSON-LD blocks
 * below never rendered in ANY environment -- which is why the live site shipped no
 * meta description, no canonical, no Open Graph tags and zero structured data.
 */
function RootShell({ children }: { children: ReactNode }) {
  const orgSchema = getOrganizationSchema();
  const websiteSchema = getWebSiteSchema();
  const gaId = import.meta.env.VITE_GA_MEASUREMENT_ID;
  const metaPixelId = import.meta.env.VITE_META_PIXEL_ID;

  return (
    <html lang="en">
      <head>
        <HeadContent />
        {/* Noscript font fallback — for users with JavaScript disabled the media=print
            swap on fontLinks never fires, so this blocking stylesheet covers them.  */}
        <noscript>
          <link rel="stylesheet" href={fontNoscriptHref} />
        </noscript>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: safeJsonLd(orgSchema) }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: safeJsonLd(websiteSchema) }}
        />
        {gaId && (
          <>
            <script async src={`https://www.googletagmanager.com/gtag/js?id=${gaId}`} />
            <script
              dangerouslySetInnerHTML={{
                __html: `
                  window.dataLayer = window.dataLayer || [];
                  function gtag(){dataLayer.push(arguments);}
                  gtag('js', new Date());
                  gtag('config', '${gaId}');
                `,
              }}
            />
          </>
        )}
        {metaPixelId && (
          <>
            <script
              dangerouslySetInnerHTML={{
                __html: `
                  !function(f,b,e,v,n,t,s)
                  {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
                  n.callMethod.apply(n,arguments):n.queue.push(arguments)};
                  if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
                  n.queue=[];t=b.createElement(e);t.async=!0;
                  t.src=v;s=b.getElementsByTagName(e)[0];
                  s.parentNode.insertBefore(t,s)}(window, document,'script',
                  'https://connect.facebook.net/en_US/fbevents.js');
                  fbq('init', '${metaPixelId}');
                  fbq('track', 'PageView');
                `,
              }}
            />
            <noscript>
              <img
                height="1"
                width="1"
                style={{ display: "none" }}
                src={`https://www.facebook.com/tr?id=${metaPixelId}&ev=PageView&noscript=1`}
                alt=""
              />
            </noscript>
          </>
        )}
      </head>
      <body>
        {/* Skip navigation link for accessibility */}
        <a href="#main-content" className="skip-nav">
          Skip to main content
        </a>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

const defaultQueryClient = new QueryClient();

function RootComponent() {
  const context = Route.useRouteContext();
  const client = context?.queryClient || defaultQueryClient;
  const location = useLocation();
  const navigate = useNavigate();
  const isAdminRoute = location.pathname.startsWith("/admin");

  useEffect(() => {
    // Ping Supabase once per 24 hours to prevent 7-day auto-pause on free tier
    void pingSupabaseFromClient();

    // Check if URL hash contains Supabase recovery token
    if (typeof window !== "undefined" && window.location.hash.includes("type=recovery")) {
      navigate({ to: "/account/reset-password" as never });
    }

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") {
        navigate({ to: "/account/reset-password" as never });
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [navigate]);

  useEffect(() => {
    trackMetaPageView();
  }, [location.pathname]);

  return (
    <QueryClientProvider client={client}>
      <CartProvider>
        <div className="flex min-h-screen flex-col">
          {!isAdminRoute && <SiteHeader />}
          <main id="main-content" className="flex-1 min-h-[calc(100vh-5rem)]">
            <Outlet />
          </main>
          {!isAdminRoute && <SiteFooter />}
          {!isAdminRoute && <CartDrawer />}
          {!isAdminRoute && <MobileTabBar />}
          {!isAdminRoute && <WhatsAppSupportButton />}
        </div>
      </CartProvider>
    </QueryClientProvider>
  );
}
